import type { Href } from "expo-router";
import { router } from "expo-router";
import { useEffect, useState } from "react";
import {
  Platform,
  Pressable,
  Text,
  useWindowDimensions,
  View,
  type ViewStyle,
} from "react-native";
import { COLORS, FONTS } from "../lib/brand";
import { PANE_THEMES } from "./brand/paneThemes";

import {
  acceptAllCookies,
  declineNonEssentialCookies,
  getCookieConsent,
} from "../lib/analytics";

const FOREST_DEEP = COLORS.FOREST_DEEP.hex;
const AMBER = COLORS.AMBER.hex;
const LINEN = COLORS.LINEN.hex;
const TEXT_DIM = "rgba(237,232,223,0.62)";
const BORDER = "rgba(181,135,58,0.22)";

const PORTAL_PREFIXES = [
  "/admin",
  "/admin-licensing",
  "/admin-rm",
  "/super-admin",
  "/client",
  "/judge",
  "/student",
  "/campus",
];

function pathIsPortal(): boolean {
  if (Platform.OS !== "web" || typeof window === "undefined") return false;
  const p = window.location.pathname;
  return PORTAL_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + "/"));
}

/**
 * Marketing-side cookie consent banner. Web-only; renders nothing on native
 * (the apps don't ship to stores yet). Visible only when there's no prior
 * decision in localStorage AND we're on a public surface — portal routes
 * skip the banner entirely so authenticated dashboards don't get a footer
 * overlay every page load.
 */
export function CookieConsentBanner() {
  const { width } = useWindowDimensions();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    if (typeof window === "undefined") return;
    if (pathIsPortal()) return;
    if (getCookieConsent() !== null) return;
    const id = window.setTimeout(() => setVisible(true), 2000);
    return () => window.clearTimeout(id);
  }, []);

  if (!visible) return null;

  const wide = width >= 720;

  return (
    <View
      // pointerEvents pass-through is the default for child Views; the banner
      // itself is interactive but doesn't trap clicks anywhere outside its
      // own bounds because it's a fixed-bottom strip, not a full-screen modal.
      style={{
        position: Platform.OS === "web" ? ("fixed" as unknown as "absolute") : "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        paddingHorizontal: wide ? 24 : 14,
        paddingBottom: wide ? 18 : 14,
        paddingTop: wide ? 18 : 14,
        alignItems: "center",
      } as ViewStyle}
    >
      <View
        style={{
          maxWidth: 720,
          width: "100%",
          backgroundColor: FOREST_DEEP,
          borderWidth: 1,
          borderColor: BORDER,
          borderRadius: 8,
          paddingHorizontal: wide ? 22 : 16,
          paddingVertical: wide ? 16 : 14,
          flexDirection: wide ? "row" : "column",
          alignItems: wide ? "center" : "stretch",
          gap: wide ? 18 : 12,
          shadowColor: COLORS.BLACK.hex,
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.4,
          shadowRadius: 18,
        }}
      >
        <Text
          style={{
            flex: 1,
            fontFamily: FONTS.body,
            fontSize: 13,
            lineHeight: 20,
            color: LINEN,
          }}
        >
          We use cookies to understand how Coravu is used and to make it work better.{" "}
          <Text
            onPress={() => router.push("/privacy" as Href)}
            style={{ color: AMBER, fontFamily: FONTS.bodyMedium }}
          >
            Read more →
          </Text>
        </Text>

        <View
          style={{
            flexDirection: "row",
            gap: 10,
            justifyContent: wide ? "flex-end" : "flex-start",
          }}
        >
          <BannerButton
            label="Essential only"
            onPress={() => {
              declineNonEssentialCookies();
              setVisible(false);
            }}
            tone="muted"
          />
          <BannerButton
            label="Accept all"
            onPress={() => {
              acceptAllCookies();
              setVisible(false);
            }}
            tone="primary"
          />
        </View>
      </View>
    </View>
  );
}

function BannerButton({
  label,
  onPress,
  tone,
}: {
  label: string;
  onPress: () => void;
  tone: "primary" | "muted";
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      style={({ hovered, pressed }) => [
        {
          paddingHorizontal: 14,
          paddingVertical: 9,
          borderRadius: 4,
          backgroundColor: tone === "primary" ? AMBER : "transparent",
          borderWidth: tone === "primary" ? 0 : 1,
          borderColor: "rgba(237,232,223,0.18)",
        } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
        Platform.OS === "web" && hovered && tone === "primary"
          ? ({ backgroundColor: COLORS.AMBER_WARM.hex } as ViewStyle)
          : null,
        Platform.OS === "web" && hovered && tone === "muted"
          ? ({ borderColor: "rgba(237,232,223,0.32)" } as ViewStyle)
          : null,
        pressed ? ({ opacity: 0.92 } as ViewStyle) : null,
      ]}
    >
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 11.2,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: tone === "primary" ? COLORS.FOREST.hex : TEXT_DIM,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}
